import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  LayoutGrid,
  SlidersHorizontal,
  Bed,
  Utensils,
  Building,
  Gift,
  Settings,
  Info,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { ROUTE } from '../../../lib/rcu/catalog/navigation';

export interface SidebarProps {
  activeRoute: string;
  onNavigate: (route: string) => void;
  expanded?: boolean;
  onToggleExpand?: () => void;
}

const getNavItems = (t: (key: string) => string) => [
  { id: 'home', label: t('nav.home'), icon: LayoutGrid, route: '/' },
  { id: 'controls', label: t('nav.roomControls'), icon: SlidersHorizontal, route: ROUTE.CONTROLS },
  { id: 'guide', label: t('nav.roomGuide'), icon: Bed, route: '/guide' },
  { id: 'dine', label: t('nav.dineIn'), icon: Utensils, route: '/dine' },
  { id: 'property', label: t('nav.propertyInfo'), icon: Building, route: '/property' },
  { id: 'offers', label: t('nav.offers'), icon: Gift, route: '/offers' },
  { id: 'settings', label: t('nav.settings'), icon: Settings, route: '/settings' },
];

export const Sidebar: React.FC<SidebarProps> = ({
  activeRoute,
  onNavigate,
  expanded = false,
  onToggleExpand,
}) => {
  const { t } = useTranslation();
  const navItems = getNavItems(t);

  return (
    <motion.aside
      className="h-screen bg-white border-r border-[#E0E0E0] flex flex-col relative rounded-r-3xl shadow-sm z-20"
      initial={false}
      animate={{ width: expanded ? 240 : 80 }}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
    >
      {/* Navigation Items */}
      <nav className="flex-1 py-6 px-2">
        <ul className="space-y-2">
          {navItems.map((item) => {
            const isActive = activeRoute === item.route;
            const Icon = item.icon;

            return (
              <li key={item.id}>
                <button
                  onClick={() => onNavigate(item.route)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200
                    focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054]
                    ${isActive
                      ? 'bg-[#FDF6E9] text-[#D4A054]'
                      : 'text-[#666666] hover:bg-[rgba(212,160,84,0.1)]'
                    }
                    ${expanded ? 'justify-start' : 'justify-center'}
                  `}
                  title={item.label}
                >
                  <div
                    className={`
                      flex items-center justify-center w-10 h-10 rounded-full transition-all duration-200
                      ${isActive ? 'bg-[#D4A054]/20' : ''}
                    `}
                  >
                    <Icon className="w-6 h-6" />
                  </div>
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -10 }}
                      className="text-sm font-medium whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Info/Support at bottom */}
      <div className="p-4 border-t border-[#E0E0E0]">
        <button
          className={`
            w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all duration-200
            text-[#666666] hover:bg-[rgba(212,160,84,0.1)]
            focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A054]
            ${expanded ? 'justify-start' : 'justify-center'}
          `}
          title={t('common.support', 'Support')}
        >
          <div className="flex items-center justify-center w-10 h-10">
            <Info className="w-6 h-6" />
          </div>
          {expanded && (
            <motion.span
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              className="text-sm font-medium whitespace-nowrap"
            >
              {t('common.support', 'Support')}
            </motion.span>
          )}
        </button>
      </div>

      {/* Expand/Collapse Toggle */}
      <button
        onClick={onToggleExpand}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-6 h-12 bg-white border border-[#E0E0E0] rounded-r-lg flex items-center justify-center text-[#666666] hover:text-[#D4A054] hover:border-[#D4A054] transition-colors duration-200 shadow-sm z-30"
        aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
      >
        {expanded ? (
          <ChevronLeft className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </button>
    </motion.aside>
  );
};

export default Sidebar;
